import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, Download, ClipboardCheck, Play, CheckCircle2, XCircle,
  Clock, AlertCircle, ChevronDown, ChevronRight, X, Bot, Search, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { projectAPI } from '../lib/api';

const statusOptions = [
  { value: 'not_executed', label: 'Not Executed', color: 'text-gray-400 bg-gray-500/10 border-gray-500/20' },
  { value: 'in_progress', label: 'In Progress', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
  { value: 'passed', label: 'Passed', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
  { value: 'failed', label: 'Failed', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
  { value: 'blocked', label: 'Blocked', color: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
];

const typeOptions = ['Functional', 'Integration', 'Edge Case', 'Security', 'Performance', 'Regression'];
const priorityOptions = ['High', 'Medium', 'Low'];

export default function TestCasesPanel({ projectId, canEdit = true }) {
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [expandedTC, setExpandedTC] = useState(null);
  const [newTC, setNewTC] = useState({
    title: '',
    requirement: '',
    type: 'Functional',
    priority: 'Medium',
    preconditions: '',
    steps: '',
    expected_result: '',
    status: 'not_executed',
    actual_result: '',
  });

  useEffect(() => {
    loadTestCases();
  }, [projectId]);

  const loadTestCases = async () => {
    try {
      const res = await projectAPI.getTestCases(projectId);
      setTestCases(res.data || []);
    } catch (err) {
      console.log('Loading test cases:', err);
      setTestCases([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTC = async () => {
    if (!newTC.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      await projectAPI.addTestCase(projectId, newTC);
      setShowAddModal(false);
      setNewTC({
        title: '', requirement: '', type: 'Functional', priority: 'Medium',
        preconditions: '', steps: '', expected_result: '', status: 'not_executed', actual_result: '',
      });
      await loadTestCases();
      toast.success('Test case added');
    } catch (err) {
      toast.error('Failed to add test case');
    }
  };

  const handleGenerateTests = async () => {
    setGenerating(true);
    try {
      const res = await projectAPI.generateDoc(projectId, { project_id: projectId, doc_type: 'generate_tests' });
      await loadTestCases();
      toast.success('Test cases generated!');
    } catch (err) {
      toast.error('Failed to generate test cases');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateStatus = async (tcId, newStatus, actualResult = '') => {
    try {
      await projectAPI.updateTestCase(projectId, tcId, { status: newStatus, actual_result: actualResult });
      await loadTestCases();
      toast.success('Test case updated');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (tcId) => {
    try {
      await projectAPI.deleteTestCase(projectId, tcId);
      await loadTestCases();
      toast.success('Deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const downloadTestCases = () => {
    let content = `# Test Cases Report

Generated: ${new Date().toLocaleString()}

## Summary
- Total: ${testCases.length}
- Passed: ${testCases.filter(t => t.status === 'passed').length}
- Failed: ${testCases.filter(t => t.status === 'failed').length}
- Not Executed: ${testCases.filter(t => t.status === 'not_executed').length}

---

`;

    testCases.forEach((tc, i) => {
      content += `## TC-${String(i + 1).padStart(3, '0')}: ${tc.title}

**Requirement:** ${tc.requirement || 'N/A'}
**Type:** ${tc.type}
**Priority:** ${tc.priority}
**Status:** ${tc.status}

**Preconditions:**
${tc.preconditions || 'None'}

**Steps:**
${tc.steps || 'N/A'}

**Expected Result:**
${tc.expected_result || 'N/A'}

**Actual Result:**
${tc.actual_result || 'N/A'}

---

`;
    });

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-cases-report.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredTCs = testCases.filter(tc => {
    const matchesSearch = tc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          tc.requirement?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || tc.status === filterStatus;
    const matchesType = filterType === 'all' || tc.type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (status) => {
    const opt = statusOptions.find(s => s.value === status) || statusOptions[0];
    return <Badge className={opt.color}>{opt.label}</Badge>;
  };

  const stats = {
    total: testCases.length,
    passed: testCases.filter(t => t.status === 'passed').length,
    failed: testCases.filter(t => t.status === 'failed').length,
    notExecuted: testCases.filter(t => t.status === 'not_executed').length,
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="h-full flex flex-col" data-testid="test-cases-panel">
      {/* Header */}
      <div className="p-4 border-b border-[var(--surface-border)]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-left">Test Cases</h2>
            <p className="text-xs opacity-60 text-left">Manage and track test case execution</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadTestCases}>
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export
            </Button>
            <Button variant="outline" size="sm" onClick={handleGenerateTests} disabled={generating}>
              <Bot className={`w-3.5 h-3.5 mr-1.5 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating...' : 'Generate'}
            </Button>
            {canEdit && (
              <Button size="sm" onClick={() => setShowAddModal(true)}>
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add TC
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="p-2 rounded-lg bg-[var(--surface-card)] border border-[var(--surface-border)] text-center">
            <div className="text-lg font-semibold">{stats.total}</div>
            <div className="text-[10px] opacity-50">Total</div>
          </div>
          <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
            <div className="text-lg font-semibold text-emerald-400">{stats.passed}</div>
            <div className="text-[10px] text-emerald-400">Passed</div>
          </div>
          <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
            <div className="text-lg font-semibold text-red-400">{stats.failed}</div>
            <div className="text-[10px] text-red-400">Failed</div>
          </div>
          <div className="p-2 rounded-lg bg-gray-500/10 border border-gray-500/20 text-center">
            <div className="text-lg font-semibold text-gray-400">{stats.notExecuted}</div>
            <div className="text-[10px] text-gray-400">Pending</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-40" />
            <Input
              placeholder="Search test cases..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px] h-8">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {statusOptions.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[130px] h-8">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {typeOptions.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {filteredTCs.length === 0 ? (
            <div className="text-center py-12 opacity-60">
              <ClipboardCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No test cases found</p>
              {canEdit && <p className="text-xs mt-1">Click "Add TC" or "Generate" to create test cases</p>}
            </div>
          ) : (
            filteredTCs.map((tc, index) => (
              <div
                key={tc.id}
                className={`rounded-lg border transition-colors ${
                  tc.status === 'failed' ? 'border-red-500/30 bg-red-500/5' :
                  tc.status === 'passed' ? 'border-emerald-500/30 bg-emerald-500/5' :
                  'border-[var(--surface-border)] bg-[var(--surface-card)]'
                }`}
              >
                <div
                  className="p-3 cursor-pointer hover:bg-[var(--surface-bg)]/30"
                  onClick={() => setExpandedTC(expandedTC === tc.id ? null : tc.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {expandedTC === tc.id ? <ChevronDown className="w-4 h-4 opacity-50" /> : <ChevronRight className="w-4 h-4 opacity-50" />}
                      <span className="text-xs font-mono opacity-50">TC-{String(index + 1).padStart(3, '0')}</span>
                      {getStatusBadge(tc.status)}
                      <Badge variant="outline" className="text-[10px]">{tc.type}</Badge>
                      <Badge variant="outline" className={`text-[10px] ${tc.priority === 'High' ? 'text-red-400 border-red-500/30' : ''}`}>{tc.priority}</Badge>
                    </div>
                    {canEdit && (
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400" onClick={(e) => { e.stopPropagation(); handleDelete(tc.id); }}>
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  <h3 className="font-medium mt-1 text-left">{tc.title}</h3>
                  {tc.requirement && <p className="text-xs opacity-50 mt-0.5 text-left">Req: {tc.requirement}</p>}
                </div>

                {expandedTC === tc.id && (
                  <div className="p-3 pt-0 border-t border-[var(--surface-border)]">
                    {tc.preconditions && (
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold mb-1 text-left">Preconditions</h4>
                        <p className="text-sm opacity-70 whitespace-pre-wrap text-left">{tc.preconditions}</p>
                      </div>
                    )}
                    {tc.steps && (
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold mb-1 text-left">Steps</h4>
                        <p className="text-sm opacity-70 whitespace-pre-wrap text-left">{tc.steps}</p>
                      </div>
                    )}
                    {tc.expected_result && (
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold mb-1 text-left">Expected Result</h4>
                        <p className="text-sm opacity-70 whitespace-pre-wrap text-left">{tc.expected_result}</p>
                      </div>
                    )}
                    {tc.actual_result && (
                      <div className="mb-3">
                        <h4 className="text-xs font-semibold mb-1 text-left">Actual Result</h4>
                        <p className="text-sm opacity-70 whitespace-pre-wrap text-left">{tc.actual_result}</p>
                      </div>
                    )}
                    {canEdit && (
                      <div className="flex gap-2 pt-2 border-t border-[var(--surface-border)]">
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => handleUpdateStatus(tc.id, 'in_progress')}>
                          <Play className="w-3 h-3 mr-1" /> Start
                        </Button>
                        <Button size="sm" className="text-xs bg-emerald-500 hover:bg-emerald-600" onClick={() => handleUpdateStatus(tc.id, 'passed')}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Pass
                        </Button>
                        <Button size="sm" className="text-xs bg-red-500 hover:bg-red-600" onClick={() => handleUpdateStatus(tc.id, 'failed')}>
                          <XCircle className="w-3 h-3 mr-1" /> Fail
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-card)] rounded-xl border border-[var(--surface-border)] w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Test Case</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Title *</label>
                <Input value={newTC.title} onChange={(e) => setNewTC({ ...newTC, title: e.target.value })} placeholder="Test case title" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Related Requirement</label>
                <Input value={newTC.requirement} onChange={(e) => setNewTC({ ...newTC, requirement: e.target.value })} placeholder="e.g., REQ-001, User Login" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Type</label>
                  <Select value={newTC.type} onValueChange={(v) => setNewTC({ ...newTC, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {typeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Priority</label>
                  <Select value={newTC.priority} onValueChange={(v) => setNewTC({ ...newTC, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {priorityOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Preconditions</label>
                <Textarea value={newTC.preconditions} onChange={(e) => setNewTC({ ...newTC, preconditions: e.target.value })} placeholder="Setup required before test" rows={2} />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Steps</label>
                <Textarea value={newTC.steps} onChange={(e) => setNewTC({ ...newTC, steps: e.target.value })} placeholder="1. Step one\n2. Step two\n3. Step three" rows={4} />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Expected Result</label>
                <Textarea value={newTC.expected_result} onChange={(e) => setNewTC({ ...newTC, expected_result: e.target.value })} placeholder="What should happen" rows={2} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleAddTC}>Add Test Case</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
